import React, { useState, useEffect, useRef } from 'react';
import { PrivyProvider, usePrivy } from '@privy-io/react-auth';

/**
 * GatewayLogin
 *
 * Minimal Privy auth component for Blade gateway pages.
 *
 * WHY THIS EXISTS:
 * Home.jsx manages Privy auth with 25+ state variables and 7 effects.
 * This component does ONE thing: login via Privy, POST the result to
 * the server, redirect to wherever the server says. All flow logic
 * lives server-side in GatewayService.
 *
 * FLOW:
 * 1. User clicks "Log In / Sign Up"
 * 2. Privy popup opens → user authenticates
 * 3. Component POSTs privy data to the server-built authenticateUrl
 *    (/api/gateway/{constellation}/{slug}/authenticate — passed in via the
 *    data-authenticate-url attribute, so the JS never builds the URL itself)
 * 4. Server returns { redirect: "/some/url" }
 * 5. Component does window.location.href = redirect (full page nav to Blade)
 *
 * DEBUG MODE:
 * When localStorage 'gateway_debug_mode' === 'true', the component pauses
 * at two breakpoints with full variable inspection:
 * - BP1: After Privy auth, before POST — shows extracted user data
 * - BP2: After server response, before redirect — shows server decision
 */

// Check debug mode from localStorage (set by gateway-debug.blade.php)
function isDebugMode() {
    try { return localStorage.getItem('gateway_debug_mode') === 'true'; } catch(e) { return false; }
}

// Debug breakpoint panel styles (matches gateway-debug.blade.php visual style)
const debugPanelStyle = {
    background: 'rgba(0,0,0,0.92)',
    color: 'white',
    borderRadius: '8px',
    fontFamily: 'monospace',
    fontSize: '11px',
    lineHeight: '1.5',
    width: '100%',
    maxWidth: '400px',
    margin: '0 auto',
    boxShadow: '0 4px 12px rgba(0,0,0,0.5)',
    overflow: 'hidden',
};

const debugHeaderStyle = (color) => ({
    padding: '8px 15px',
    background: color,
    color: 'white',
    fontWeight: 'bold',
    fontSize: '10px',
    textTransform: 'uppercase',
    letterSpacing: '1px',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
});

const debugBodyStyle = {
    padding: '10px 15px',
};

const debugRowStyle = {
    marginBottom: '4px',
    display: 'flex',
    gap: '6px',
};

const debugKeyStyle = {
    color: '#8bb8c8',
    flexShrink: 0,
};

const debugValueStyle = {
    color: '#ccc',
    wordBreak: 'break-all',
};

const debugBtnStyle = {
    width: '100%',
    padding: '10px 16px',
    background: '#28a745',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontFamily: 'monospace',
    fontWeight: 'bold',
    fontSize: '13px',
    marginTop: '8px',
};

/**
 * DebugBreakpoint — renders a debug panel showing variables with a Continue button.
 */
function DebugBreakpoint({ title, color, data, buttonLabel, onContinue }) {
    return (
        <div style={debugPanelStyle}>
            <div style={debugHeaderStyle(color)}>
                <span style={{
                    display: 'inline-block', width: '8px', height: '8px',
                    background: '#ff5252', borderRadius: '50%',
                    animation: 'gwDbBlink 1s ease-in-out infinite',
                }}></span>
                <span>BREAKPOINT: {title}</span>
            </div>
            <div style={debugBodyStyle}>
                {Object.entries(data).map(([key, value]) => (
                    <div key={key} style={debugRowStyle}>
                        <span style={debugKeyStyle}>{key}:</span>
                        <span style={debugValueStyle}>
                            {value === null || value === undefined
                                ? '<null>'
                                : typeof value === 'object'
                                    ? JSON.stringify(value)
                                    : String(value)
                            }
                        </span>
                    </div>
                ))}
                <button style={debugBtnStyle} onClick={onContinue}>
                    {buttonLabel} →
                </button>
            </div>
        </div>
    );
}


function LoginButton({ gatewaySlug, csrfToken, authenticateUrl, resolveExistingUrl, signOutUrl, invitationCode, roleType, supportEmail, orgName }) {
    const { ready, authenticated, user: privyUser, login, logout } = usePrivy();
    // Restore privy_pending across component remounts and full page reloads
    // (Google OAuth redirects away). WHY the timestamp: if the flag is older
    // than ~90s, treat it as stale (popup dismissed, OAuth abandoned, tab left
    // open overnight, etc.) so the user doesn't get stuck on a Signing-in
    // spinner from a previous session that never resolved.
    //
    // WHY slug-scoped keys: sessionStorage is per-tab. Without scoping, a
    // pending login on gateway A leaks to gateway B if the user navigates
    // there in the same tab — they'd see a "Signing in…" spinner and no
    // login button. Keying by slug isolates each gateway's pending state.
    const PENDING_MAX_AGE_MS = 90 * 1000;
    const pendingKey = `gw_login_pending_${gatewaySlug || 'default'}`;
    const pendingAtKey = `gw_login_pending_at_${gatewaySlug || 'default'}`;
    const [status, setStatusRaw] = useState(() => {
        try {
            if (sessionStorage.getItem(pendingKey) !== '1') return 'idle';
            const stamp = parseInt(sessionStorage.getItem(pendingAtKey) || '0', 10);
            if (!stamp || (Date.now() - stamp) > PENDING_MAX_AGE_MS) {
                sessionStorage.removeItem(pendingKey);
                sessionStorage.removeItem(pendingAtKey);
                return 'idle';
            }
            return 'privy_pending';
        } catch (e) { return 'idle'; }
    });
    const setStatus = (s) => {
        try {
            if (s === 'privy_pending') {
                sessionStorage.setItem(pendingKey, '1');
                sessionStorage.setItem(pendingAtKey, String(Date.now()));
            } else {
                sessionStorage.removeItem(pendingKey);
                sessionStorage.removeItem(pendingAtKey);
            }
        } catch(e) {}
        setStatusRaw(s);
    };
    // idle | privy_pending | debug_pre_post | authenticating | debug_pre_redirect | redirecting | error
    // | existing_user_check | existing_user_known | existing_user_unknown
    const [errorMessage, setErrorMessage] = useState(null);
    const hasProcessed = useRef(false);
    const isLoggingOut = useRef(
        new URLSearchParams(window.location.search).get('logout') === 'true'
    );
    // Sticky for the whole page view, unlike isLoggingOut which flips back to
    // false once Privy's logout() resolves. After an explicit ?logout=true we
    // must never fall back into the "already signed in" State B card or
    // silently re-POST the lingering session — even when Privy's logout()
    // resolved without actually clearing it. That's the Safari case: ITP keeps
    // the Privy session alive, logout() reports success but the session
    // persists, and the user would otherwise be bounced straight back into
    // "already signed in" with no login button. We stay on the login button
    // and only proceed on a real click (which moves status away from 'idle').
    const suppressExistingUserCard = useRef(
        new URLSearchParams(window.location.search).get('logout') === 'true'
    );
    const [debugData, setDebugData] = useState(null);       // BP1: extracted privy data
    const [serverResponse, setServerResponse] = useState(null); // BP2: server response
    const debug = useRef(isDebugMode());
    const originalTitle = useRef(null); // Store Blade title for restoring after error
    const eagerClick = useRef(false); // User clicked Blade button before React mounted
    // State B: Privy reports the user as authenticated but we never initiated
    // a login this session (no pending flag, no eager click, no button press).
    // That means the Privy session predates this page — the user is signed
    // in via another gateway and shouldn't be silently onboarded here.
    const [existingUserInfo, setExistingUserInfo] = useState(null);
    const existingUserCheckAttempted = useRef(false);

    // Check if user clicked the server-rendered button before React took over
    useEffect(() => {
        try {
            if (sessionStorage.getItem('gw_eager_click') === '1') {
                sessionStorage.removeItem('gw_eager_click');
                eagerClick.current = true;
            }
        } catch(e) {}
    }, []);

    // Auto-trigger login when Privy is ready if user clicked eagerly
    useEffect(() => {
        if (!ready || authenticated || eagerClick.current !== true) return;
        eagerClick.current = false; // consume the flag
        setStatus('privy_pending');
        login();
    }, [ready, authenticated, login]);

    // Handle ?debug_error= — show error state immediately (from debug scenario buttons)
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const debugError = params.get('debug_error');
        if (debugError) {
            setStatus('error');
            setErrorMessage(debugError);
            // Clean up URL
            params.delete('debug_error');
            const cleanUrl = params.toString()
                ? `${window.location.pathname}?${params}`
                : window.location.pathname;
            window.history.replaceState({}, '', cleanUrl);
        }
    }, []);

    // Handle ?logout=true — clear Privy session before anything else
    useEffect(() => {
        if (!ready || !isLoggingOut.current) return;

        logout().then(() => {
            isLoggingOut.current = false;
            // Clean up the URL
            const params = new URLSearchParams(window.location.search);
            params.delete('logout');
            const cleanUrl = params.toString()
                ? `${window.location.pathname}?${params}`
                : window.location.pathname;
            window.history.replaceState({}, '', cleanUrl);
        });
    }, [ready, logout]);

    // No auto-detection of Privy modal dismiss — we can't reliably distinguish
    // "user closed popup" from "Privy SDK doing backend work after popup closed."
    // Instead, privy_pending shows a spinner with a Cancel link for manual reset.

    // When Privy authenticates, extract data (and pause if debug mode)
    useEffect(() => {
        if (!ready || !authenticated || !privyUser || hasProcessed.current || isLoggingOut.current) return;

        // Arrived via an explicit sign-out: stay on the login button. Don't run
        // the "already signed in" flow and don't auto-POST the lingering Privy
        // session. Only a real click (which sets status away from 'idle')
        // proceeds past this point. See suppressExistingUserCard above.
        if (suppressExistingUserCard.current && status === 'idle') return;

        // State B detection: if status is still 'idle' here, no user action
        // initiated the login in this component (no pending flag, no eager
        // click). The Privy session pre-existed — show the "already signed
        // in" card instead of POSTing them straight into onboarding. The
        // attempted-ref keeps this from re-entering after the resolve
        // completes and the user makes a choice.
        if (status === 'idle' && resolveExistingUrl && !existingUserCheckAttempted.current) {
            existingUserCheckAttempted.current = true;
            setStatus('existing_user_check');
            return;
        }

        hasProcessed.current = true;

        // Extract email from Privy user
        let email = privyUser.email?.address;
        if (!email && privyUser.linkedAccounts) {
            const withEmail = privyUser.linkedAccounts.find(a => a.email);
            if (withEmail) email = withEmail.email;
        }

        // Extract name (LinkedIn preferred, then email prefix)
        let name = privyUser.name;
        if (!name) {
            const linkedIn = privyUser.linkedAccounts?.find(
                a => a.type === 'linkedin_oidc' || a.type === 'linkedin_oauth'
            );
            name = linkedIn?.name || (email ? email.split('@')[0] : null);
        }

        // Extract LinkedIn ID
        const linkedInAccount = privyUser.linkedAccounts?.find(
            a => a.type === 'linkedin_oidc' || a.type === 'linkedin_oauth'
        );

        // Determine login method
        let loginMethod = 'email';
        if (linkedInAccount) loginMethod = 'linkedin';
        else if (privyUser.linkedAccounts?.find(a => a.type === 'google_oauth')) loginMethod = 'google';

        const payload = {
            privy_id: privyUser.id,
            email: email,
            name: name,
            linkedin_id: linkedInAccount?.subject || null,
            login_method: loginMethod,
            invitation_code: invitationCode,
            role_type: roleType,
        };

        // DEBUG BP1: Pause before POST
        if (debug.current) {
            console.log('[GatewayDebug] BP1: Privy auth complete — pausing before POST', payload);
            setDebugData(payload);
            setStatus('debug_pre_post');
            return;
        }

        // Normal flow: POST immediately
        postToServer(payload);
    }, [ready, authenticated, privyUser, authenticateUrl, csrfToken, invitationCode, roleType, status, resolveExistingUrl]);

    // State B: resolve the existing Privy session against our server. The
    // response tells us whether this Privy ID has an account on the platform
    // and, if so, what their home dashboard + galaxy are.
    useEffect(() => {
        if (status !== 'existing_user_check' || !privyUser?.id || !resolveExistingUrl) return;

        fetch(resolveExistingUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                'X-CSRF-TOKEN': csrfToken,
            },
            body: JSON.stringify({ privy_id: privyUser.id }),
        })
            .then(r => r.ok ? r.json() : Promise.reject(r))
            .then(data => {
                setExistingUserInfo(data);
                setStatusRaw(data && data.found ? 'existing_user_known' : 'existing_user_unknown');
            })
            .catch(() => {
                // Resolve failed — show the unknown-user variant so the
                // founder can still proceed if they want, or sign out and
                // try again. Better than silently bouncing them into the
                // onboarding flow they were trying to avoid.
                setExistingUserInfo(null);
                setStatusRaw('existing_user_unknown');
            });
    }, [status, privyUser?.id, resolveExistingUrl, csrfToken]);

    // POST to server
    function postToServer(payload) {
        setStatus('authenticating');

        fetch(authenticateUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                'X-CSRF-TOKEN': csrfToken,
            },
            credentials: 'include',
            body: JSON.stringify(payload),
        })
            .then(res => res.json())
            .then(data => {
                // DEBUG BP2: Pause before redirect
                if (debug.current) {
                    console.log('[GatewayDebug] BP2: Server response — pausing before redirect', data);
                    setServerResponse(data);
                    setStatus('debug_pre_redirect');
                    return;
                }

                // Normal flow: follow redirect
                handleServerResponse(data);
            })
            .catch(() => {
                setStatus('error');
                setErrorMessage('Connection error. Please try again.');
            });
    }

    // Handle server response (redirect or error)
    function handleServerResponse(data) {
        if (data.redirect) {
            setStatus('redirecting');
            window.location.href = data.redirect;
        } else if (data.error) {
            setStatus('error');
            setErrorMessage(data.error);
        }
    }

    // ── DEBUG BP1: After Privy, before POST ──
    if (status === 'debug_pre_post' && debugData) {
        return (
            <DebugBreakpoint
                title="POST-PRIVY / PRE-POST"
                color="#e65100"
                data={debugData}
                buttonLabel="Continue — POST to server"
                onContinue={() => postToServer(debugData)}
            />
        );
    }

    // ── DEBUG BP2: After server response, before redirect ──
    if (status === 'debug_pre_redirect' && serverResponse) {
        return (
            <DebugBreakpoint
                title="POST-SERVER / PRE-REDIRECT"
                color="#1565c0"
                data={serverResponse}
                buttonLabel="Continue — Follow redirect"
                onContinue={() => handleServerResponse(serverResponse)}
            />
        );
    }

    // Signing in / syncing / redirecting states
    if (status === 'privy_pending' || status === 'authenticating' || status === 'redirecting') {
        const label = status === 'redirecting'
            ? 'Redirecting...'
            : status === 'authenticating'
                ? 'Syncing with database...'
                : 'Signing in...';
        return (
            <div className="text-center py-3">
                <div className="spinner-border spinner-border-sm text-primary me-2" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
                <span className="text-muted">{label}</span>
                {status === 'privy_pending' && (
                    <div style={{ marginTop: '12px' }}>
                        <button
                            onClick={() => { hasProcessed.current = false; setStatus('idle'); logout(); }}
                            style={{ background: 'none', border: 'none', color: '#6c757d', fontSize: '13px', cursor: 'pointer', textDecoration: 'underline' }}
                        >
                            Cancel
                        </button>
                    </div>
                )}
            </div>
        );
    }

    // Error state — update the Blade title and show styled error
    if (status === 'error') {
        // Change the Blade-rendered title to reflect the error
        const titleEl = document.querySelector('.auth-card__title');
        if (titleEl) {
            if (!originalTitle.current) originalTitle.current = titleEl.textContent;
            titleEl.textContent = 'Unable to Sign In';
        }

        const mailto = supportEmail
            ? `mailto:${supportEmail}?subject=${encodeURIComponent('Access Request — ' + (orgName || 'Organization'))}`
            : null;

        // Linkify the "contact an administrator" phrase inside the message
        // body so the user has a single clear path forward. Falls back to
        // plain text when no support email is configured.
        const renderMessageBody = () => {
            const phrase = 'contact an administrator';
            const parts = (errorMessage || '').split(new RegExp(`(${phrase})`, 'i'));
            return parts.map((part, idx) =>
                mailto && part.toLowerCase() === phrase.toLowerCase()
                    ? <a key={idx} href={mailto} style={{ color: 'inherit', textDecoration: 'underline' }}>{part}</a>
                    : <span key={idx}>{part}</span>
            );
        };

        return (
            <div style={{ textAlign: 'left' }}>
                <p style={{
                    color: '#181C25',
                    fontSize: '14px',
                    lineHeight: '1.5',
                    marginBottom: '20px',
                }}>{renderMessageBody()}</p>
                {mailto && (
                    <a href={mailto} className="auth-card__button" style={{ display: 'block', textAlign: 'center', textDecoration: 'none' }}>
                        Contact Support
                    </a>
                )}
                <button
                    className="auth-card__button"
                    style={{ marginTop: '10px', background: '#64748b' }}
                    onClick={() => {
                        hasProcessed.current = false;
                        setStatus('idle');
                        setErrorMessage(null);
                        // Restore original Blade title
                        const el = document.querySelector('.auth-card__title');
                        if (el && originalTitle.current) el.textContent = originalTitle.current;
                        logout();
                    }}
                >
                    Try Again
                </button>
            </div>
        );
    }

    // State B card — Privy session pre-existed, the user was about to be
    // silently onboarded. Show them what we know and let them choose:
    // continue to their existing dashboard, also join this galaxy, or
    // sign out and sign in as someone else.
    if (status === 'existing_user_check') {
        return (
            <div className="text-center py-3">
                <div className="spinner-border spinner-border-sm text-primary me-2" role="status"></div>
                <span className="text-muted">Checking your session…</span>
            </div>
        );
    }

    if (status === 'existing_user_known' || status === 'existing_user_unknown') {
        const info = existingUserInfo || {};
        const known = status === 'existing_user_known';
        const email = info.email || privyUser?.email?.address || 'your account';
        const targetGalaxy = orgName || 'this galaxy';
        const signOutHref = signOutUrl || '/';
        const onAlsoJoin = () => {
            // Skip the State B gate (already attempted) and run the normal
            // extract + POST flow. setStatus is in the auth useEffect's deps,
            // so flipping status retriggers it.
            setStatus('privy_pending');
        };
        return (
            <div>
                <div className="success-card__info" style={{ marginBottom: 16 }}>
                    {known ? (
                        <>
                            <div className="success-card__info-row">
                                <span className="success-card__info-label">Signed in as</span>
                                <span className="success-card__info-value">{email}</span>
                            </div>
                            {info.home_constellation_name && (
                                <div className="success-card__info-row">
                                    <span className="success-card__info-label">Your home galaxy</span>
                                    <span className="success-card__info-value">{info.home_constellation_name}</span>
                                </div>
                            )}
                        </>
                    ) : (
                        <div className="success-card__info-row">
                            <span className="success-card__info-label">Signed in via Privy as</span>
                            <span className="success-card__info-value">{email}</span>
                        </div>
                    )}
                </div>

                {known && info.dashboard_url && (
                    <a href={info.dashboard_url} className="success-card__button" style={{ marginBottom: 12, display: 'block', textAlign: 'center' }}>
                        Go to my dashboard
                    </a>
                )}

                <button
                    type="button"
                    className="auth-card__button"
                    onClick={onAlsoJoin}
                    style={known ? { background: 'transparent', color: 'inherit', border: '1px solid currentColor' } : {}}
                >
                    {known ? `Also join ${targetGalaxy}` : `Sign up for ${targetGalaxy}`}
                </button>

                <a
                    href={signOutHref}
                    className="auth-card__description"
                    style={{ display: 'block', marginTop: 12, textAlign: 'center', fontSize: 13, opacity: 0.75, textDecoration: 'underline' }}
                >
                    Sign in as someone else
                </a>
            </div>
        );
    }

    // Normal state — show login button
    return (
        <button
            className="auth-card__button"
            onClick={() => { setStatus('privy_pending'); login(); }}
            disabled={!ready}
            style={!ready ? { opacity: 0.6, cursor: 'wait' } : {}}
        >
            {!ready ? 'Loading...' : 'Login or Sign up with Privy'}
        </button>
    );
}

/**
 * Wrapper that provides PrivyProvider context.
 * Config matches the existing app.js PrivyProvider setup.
 */
export default function GatewayLogin(props) {
    return (
        <PrivyProvider
            appId={props.appId}
            config={{
                appearance: {
                    accentColor: '#A7C080',
                    theme: '#FFFFFF',
                    showWalletLoginFirst: false,
                },
                loginMethods: ['email', 'google', 'linkedin'],
            }}
        >
            <LoginButton
                gatewaySlug={props.gatewaySlug}
                csrfToken={props.csrfToken}
                authenticateUrl={props.authenticateUrl}
                resolveExistingUrl={props.resolveExistingUrl}
                signOutUrl={props.signOutUrl}
                invitationCode={props.invitationCode}
                roleType={props.roleType}
                supportEmail={props.supportEmail}
                orgName={props.orgName}
            />
        </PrivyProvider>
    );
}
